'use client';

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

/**
 * Shared by the desktop search bar and the mobile search overlay — same
 * localStorage key, so a search saved on one surface shows up on the other.
 * Loaded in an effect rather than a lazy initializer: both call sites render
 * on the client only, but starting from `[]` avoids a hydration mismatch if
 * that ever changes.
 */
export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setRecentSearches(JSON.parse(stored).slice(0, MAX_RECENT_SEARCHES));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const saveRecentSearch = (term: string) => {
    setRecentSearches((prev) => {
      const updated = [term, ...prev.filter((s) => s !== term)].slice(0, MAX_RECENT_SEARCHES);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  };

  const clearRecentSearch = (term: string) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((s) => s !== term);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Ignore localStorage errors
      }
      return updated;
    });
  };

  const clearAllRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore localStorage errors
    }
  };

  return { recentSearches, saveRecentSearch, clearRecentSearch, clearAllRecentSearches };
}
